'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Search, Play, Sparkles } from 'lucide-react';
import NetflixCarousel from '@/components/gallery/NetflixCarousel';
import WatchlistButton from '@/components/gallery/WatchlistButton';
import FavoriteButton from '@/components/gallery/FavoriteButton';
import SubscribeModal from '@/components/gallery/SubscribeModal';
import Link from 'next/link';

interface Book {
  id: string;
  title: string;
  coverImageUrl: string | null;
  totalDuration?: number | null;
  ageBucket?: string | null;
  language?: string;
  pageCount?: number;
}

export default function LibraryPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  
  const [continueWatching, setContinueWatching] = useState<Book[]>([]);
  const [watchlist, setWatchlist] = useState<Book[]>([]);
  const [favorites, setFavorites] = useState<Book[]>([]);
  const [myBooks, setMyBooks] = useState<Book[]>([]);
  const [publicBooks, setPublicBooks] = useState<Book[]>([]);
  const [montessoriBooks, setMontessoriBooks] = useState<Book[]>([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [watchlistIds, setWatchlistIds] = useState<Set<string>>(new Set());
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());

  const isSubscriber = session?.user?.isSubscriber || false;
  const isPro = session?.user?.subscriptionPlan === 'AMAYA_PRO';

  useEffect(() => {
    if (status === 'unauthenticated') {
      router.push('/login?callbackUrl=/audio-book/library');
    }
  }, [status, router]);

  useEffect(() => {
    if (status === 'authenticated') {
      loadLibraryData();
    }
  }, [status]);

  const loadLibraryData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [
        historyRes,
        watchlistRes,
        favoritesRes,
        myBooksRes,
        publicBooksRes,
      ] = await Promise.all([
        fetch('/api/watch-history'),
        fetch('/api/watchlist'),
        fetch('/api/favorites'),
        fetch('/api/audio-book/my-books'),
        fetch('/api/audio-book/public'),
      ]);

      if (historyRes.ok) {
        const data = await historyRes.json();
        setContinueWatching(data.history?.map((h: any) => h.book).filter(Boolean) || []);
      }

      if (watchlistRes.ok) {
        const data = await watchlistRes.json();
        const books = data.watchlist?.map((w: any) => w.book).filter(Boolean) || [];
        setWatchlist(books);
        setWatchlistIds(new Set(books.map((b: Book) => b.id)));
      }

      if (favoritesRes.ok) {
        const data = await favoritesRes.json();
        const books = data.favorites?.map((f: any) => f.book).filter(Boolean) || [];
        setFavorites(books);
        setFavoriteIds(new Set(books.map((b: Book) => b.id)));
      }

      if (myBooksRes.ok) {
        const data = await myBooksRes.json();
        const books = data.books || [];
        setMyBooks(books);
        
        // Filter Montessori books (Pro feature)
        if (isPro) {
          setMontessoriBooks(books.filter((b: any) => b.montessoriMode));
        }
      }

      if (publicBooksRes.ok) {
        const data = await publicBooksRes.json();
        setPublicBooks(data.books || []);
      }
    } catch (error) {
      console.error('Failed to load library:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWatchlistToggle = (bookId: string, added: boolean) => {
    if (added) {
      setWatchlistIds(prev => new Set(prev).add(bookId));
    } else {
      setWatchlistIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(bookId);
        return newSet;
      });
      // Remove from watchlist display
      setWatchlist(prev => prev.filter(b => b.id !== bookId));
    }
  };

  const handleFavoriteToggle = (bookId: string, added: boolean) => {
    if (added) {
      setFavoriteIds(prev => new Set(prev).add(bookId));
    } else {
      setFavoriteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(bookId);
        return newSet;
      });
      // Remove from favorites display
      setFavorites(prev => prev.filter(b => b.id !== bookId));
    }
  };

  const handleBookClick = (bookId: string, isPublic: boolean) => {
    if (isPublic && !isSubscriber) {
      setSelectedBook(bookId);
      setShowSubscribeModal(true);
    } else {
      router.push(`/audio-book/${bookId}`);
    }
  };

  const renderEnhancedBookCard = (book: Book, isPublic = false) => (
    <div
      key={book.id}
      style={{
        minWidth: '280px',
        aspectRatio: '16/9',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: book.coverImageUrl
          ? `url(${book.coverImageUrl}) center/cover`
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}
      onClick={() => handleBookClick(book.id, isPublic)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05) translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.3)';
        e.currentTarget.style.zIndex = '10';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1) translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        e.currentTarget.style.zIndex = '1';
      }}
    >
      {/* Gradient Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)',
        }}
      />

      {/* Action Buttons */}
      <div
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          display: 'flex',
          gap: '8px',
          zIndex: 2,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <WatchlistButton
          bookId={book.id}
          isInWatchlist={watchlistIds.has(book.id)}
          onToggle={handleWatchlistToggle}
        />
        <FavoriteButton
          bookId={book.id}
          isFavorite={favoriteIds.has(book.id)}
          onToggle={handleFavoriteToggle}
        />
      </div>

      {/* Play Button (on hover) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          background: 'rgba(0, 0, 0, 0.3)',
        }}
        className="play-overlay"
      >
        <div
          style={{
            width: '60px',
            height: '60px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Play size={28} color="#667eea" fill="#667eea" style={{ marginLeft: '4px' }} />
        </div>
      </div>

      {/* Book Info */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
        }}
      >
        <h3
          style={{
            color: 'white',
            fontSize: '1rem',
            fontWeight: 700,
            margin: '0 0 8px 0',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {book.title}
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {book.totalDuration && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 600,
              }}
            >
              {Math.ceil(book.totalDuration / 60)}min
            </span>
          )}
          {book.ageBucket && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 600,
              }}
            >
              {book.ageBucket}
            </span>
          )}
          {isPublic && !isSubscriber && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'white',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 700,
              }}
            >
              PRO
            </span>
          )}
        </div>
      </div>

      <style jsx>{`
        div:hover .play-overlay {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );

  const filteredPublicBooks = searchQuery
    ? publicBooks.filter(book =>
        book.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : publicBooks;

  if (status === 'loading' || loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '6px solid rgba(255, 255, 255, 0.3)',
              borderTopColor: 'white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }}
          />
          <p style={{ color: 'white', fontSize: '1.25rem', fontWeight: 600 }}>
            Loading your library...
          </p>
        </div>
        <style jsx>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f0f0f 0%, #1a1a2e 100%)',
        paddingTop: '80px',
        paddingBottom: '60px',
      }}
    >
      {/* Hero Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          padding: '60px 24px',
          marginBottom: '40px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 900,
              color: 'white',
              margin: '0 0 16px 0',
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          >
            Your Library
          </h1>
          <p
            style={{
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: '0 0 32px 0',
              maxWidth: '600px',
            }}
          >
            Continue your magical storytelling journey
          </p>

          {/* Search Bar */}
          <div style={{ maxWidth: '600px' }}>
            <div
              style={{
                position: 'relative',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Search
                size={24}
                color="#9ca3af"
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search stories..."
                style={{
                  width: '100%',
                  padding: '18px 20px 18px 60px',
                  border: 'none',
                  fontSize: '1rem',
                  background: 'transparent',
                  color: '#1a1a1a',
                  outline: 'none',
                }}
              />
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div
          style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />
      </div>

      {/* Carousels */}
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Continue Watching */}
        {continueWatching.length > 0 && (
          <NetflixCarousel
            title="Continue Watching"
            books={continueWatching}
            renderBookCard={(book) => renderEnhancedBookCard(book, false)}
          />
        )}

        {/* My Watchlist */}
        {watchlist.length > 0 && (
          <NetflixCarousel
            title="My Watchlist"
            books={watchlist}
            renderBookCard={(book) => renderEnhancedBookCard(book, false)}
          />
        )}

        {/* My Favorites */}
        {favorites.length > 0 && (
          <NetflixCarousel
            title="❤️ My Favorites"
            books={favorites}
            renderBookCard={(book) => renderEnhancedBookCard(book, false)}
          />
        )}

        {/* My Created Books */}
        {myBooks.length > 0 && (
          <NetflixCarousel
            title="My Created Stories"
            books={myBooks}
            renderBookCard={(book) => renderEnhancedBookCard(book, false)}
          />
        )}

        {/* Montessori Collection (Pro Only) */}
        {isPro && montessoriBooks.length > 0 && (
          <NetflixCarousel
            title="🧠 Montessori Collection"
            books={montessoriBooks}
            renderBookCard={(book) => renderEnhancedBookCard(book, false)}
          />
        )}

        {/* Public Stories */}
        {filteredPublicBooks.length > 0 && (
          <NetflixCarousel
            title={searchQuery ? `Search Results (${filteredPublicBooks.length})` : '🌟 Discover Stories'}
            books={filteredPublicBooks}
            renderBookCard={(book) => renderEnhancedBookCard(book, true)}
          />
        )}

        {/* Empty State */}
        {!loading &&
          continueWatching.length === 0 &&
          watchlist.length === 0 &&
          favorites.length === 0 &&
          myBooks.length === 0 &&
          publicBooks.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '80px 24px',
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              <Sparkles size={64} color="rgba(255, 255, 255, 0.3)" style={{ marginBottom: '24px' }} />
              <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '16px', color: 'white' }}>
                Your library is empty
              </h2>
              <p style={{ fontSize: '1.125rem', marginBottom: '32px' }}>
                Start creating magical stories for your children!
              </p>
              <Link href="/audio-book/create">
                <button
                  style={{
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Sparkles size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                  Create Your First Story
                </button>
              </Link>
            </div>
          )}
      </div>

      {/* Subscribe Modal */}
      <SubscribeModal
        isOpen={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        bookTitle={selectedBook ? publicBooks.find(b => b.id === selectedBook)?.title : undefined}
      />
    </div>
  );
}

